import { Entitlements } from '@protector/entities/licensing/constants';
import { SetUseNewMapColors } from '@protector/entities/property/__legacy__/redux.actions';
import { Spin } from 'antd';
import { Layout } from 'antdV3';
import { C_LEVEL_REPORT } from 'authentication/constants';
import { useDidMount, useMediaQuery } from 'beautiful-react-hooks';
import { PROTECTOR_APP_ID } from 'config/constants';
import { MEDIA_QUERIES } from 'config/style/breakpoints';
import STMenu from 'core/components/menu/menu.component';
import 'core/components/menu/menu.styles.less';
import TopBarContainer from 'core/components/topbar/topbar.container';
import STTrigger from 'core/components/trigger/trigger.component';
import { getLicensingStatus, getSystemFlags } from 'core/core.selectors';
import ZendeskWidget from 'core/shared/zendesk/zendeskWidget';
import { validatePlansAndEntitlements } from 'core/utils/functions';
import { useLoadFeatureFlags } from 'core/utils/hooks/feature.flags.hook';
import { useCompanyOrOrganizationMatch, useWorkspaceMatch } from 'core/utils/hooks/usePathMatches';
import { FeatureLoadTimingProvider } from 'core/utils/segment/feature-load-timing.context';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import {
  LoadCompanies,
  LoadCurrentWorkspaceId,
  LoadOrgLicensingStatus,
  LoadWorkspaceLicensingStatus
} from 'entities/company/company.actions';
import { getCurrentWorkspaceId, getSelectedCompany } from 'entities/company/company.selectors';
import { LoadProducts } from 'entities/product/product.actions';
import { Suspense, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Unless } from 'react-if';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, matchPath, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { IndexedDBPromiseAPI } from '../core/indexeddb.utils';
import { getZendeskWidgetKey } from '../core/utils/functions';
import HallFooterMobileButtons from './hall-footer/hall-footer-mobile-buttons.component';

const { Content, Sider } = Layout;

interface STSiderHandle {
  toggle: () => void;
}

interface STSiderProps {
  hidden?: boolean;
  isMobile: boolean;
  hideSideBarInMobile: boolean;
  onCollapseMobile?: () => void;
}

const STSider = forwardRef<STSiderHandle, STSiderProps>(({ hidden, isMobile, hideSideBarInMobile, onCollapseMobile }, ref) => {
  const [collapsed, setCollapsed] = useState(true);

  const matchWorkspace = useWorkspaceMatch();
  const matchCompany = useCompanyOrOrganizationMatch();

  const matchFarmSettingsInputsMgt = useMatch({ path: '/farm-settings/inputs-mgt/*', end: false });
  const matchPowerBIReports = useMatch({ path: '/workspace/:workspaceId/reports', end: false });

  const hideMenuInReaPath = !!useMatch({ path: '/company/:companyId/property/:propertyId/integrations/rea/*' });

  const segmentTracking = useSegmentTracking();

  const handleClick = useCallback(() => {
    segmentTracking.track(collapsed ? 'Expanded sidebar' : 'Collapsed sidebar');

    const shouldHideSidebar = hideSideBarInMobile && isMobile && !collapsed;
    setCollapsed(prev => !prev);
    if (shouldHideSidebar) {
      onCollapseMobile?.();
    }
  }, [collapsed, hideSideBarInMobile, isMobile, onCollapseMobile, segmentTracking]);

  useImperativeHandle(
    ref,
    () => ({
      toggle: handleClick
    }),
    [handleClick]
  );
  const showMenu =
    !!matchCompany ||
    !!matchFarmSettingsInputsMgt ||
    !!matchWorkspace ||
    !!matchPowerBIReports ||
    (!!hideSideBarInMobile && isMobile && !collapsed);

  return (
    <Unless condition={hideMenuInReaPath}>
      <Sider
        data-testid='sd-sidebar'
        className='st-sider'
        trigger={null}
        collapsible
        collapsed={collapsed}
        collapsedWidth={64}
        width={250}
        style={hidden ? { display: 'none' } : undefined}>
        <div className='st-menu'>{showMenu && <STMenu />}</div>

        <STTrigger onClick={handleClick} collapsed={collapsed} />
      </Sider>
    </Unless>
  );
});

const MainLayout = () => {
  const [showZendesk, setShowZendesk] = useState<boolean>(false);
  const [zendeskWidgetKey, setzendeskWidgetKey] = useState<string>();
  const navigate = useNavigate();
  const systemFlags = useSelector(getSystemFlags);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const siderRef = useRef<STSiderHandle | null>(null);

  const isMobile = useMediaQuery(MEDIA_QUERIES.MOBILE);
  const hideSideBarInMobile = Boolean(systemFlags?.P40_54863_hide_sidebar_menu_in_mobile);
  const [isSiderVisible, setIsSiderVisible] = useState(!isMobile);

  const hideMenuInProductDetailsPath = !!useMatch({ path: '/product-details/:productId' });
  const hideMenuInOrganizationCreatePath = !!useMatch({ path: '/workspace/:workspaceId/organization-create' });
  const hideMenuInOrganizationEditPath = !!useMatch({ path: '/workspace/:workspaceId/organization-edit/:organizationId' });
  const isDiseaseRiskMFEPage = pathname.includes('/disease-risk-by-property');
  const isRiskManagementWebviewPage = pathname.startsWith('/risk-management');
  const isSubscriptionManagementEmbeddedPage = pathname.includes('/subscription-management-embedded');
  const isSubscribeMyselfEmbeddedPage = pathname.includes('/subscribe-myself-embedded');
  const isAiChatbotEmbeddedPage = pathname.includes('/ai-chatbot-embedded');
  const isAgOperationCreateEmbeddedPage = pathname.includes('/ag-operation-create-embedded');
  const isAgOperationListEmbeddedPage = pathname.includes('/ag-operation-list-embedded');
  const isEmbeddedWebviewPage =
    isSubscriptionManagementEmbeddedPage ||
    isSubscribeMyselfEmbeddedPage ||
    isAiChatbotEmbeddedPage ||
    isAgOperationCreateEmbeddedPage ||
    isAgOperationListEmbeddedPage;
  /*
   * The reports page app renders the desktop whole screen inside a mobile WebView, which is
   * what P40-61685 embeds. Unlike the disease-risk MFE above, this route stays chrome-less only on mobile.
   */
  const isReportsListWebviewPage = pathname.includes('/reports-list') && isMobile;
  const isCLevelReportPage = pathname.includes(C_LEVEL_REPORT);
  /* Registered under both `/company/:companyId` and `/workspace/:workspaceId`. */
  const matchCompanyVisitDashboard = useMatch({ path: '/company/:companyId/visit-dashboard' });
  const matchWorkspaceVisitDashboard = useMatch({ path: '/workspace/:workspaceId/visit-dashboard' });
  const isVisitDashboardPage = !!matchCompanyVisitDashboard || !!matchWorkspaceVisitDashboard;
  useLoadFeatureFlags();

  const selectedCompany = useSelector(getSelectedCompany);
  const companyId = selectedCompany?.id;

  const workspaceIdFromStore = useSelector(getCurrentWorkspaceId);
  const workspaceIdFromRoute = useWorkspaceMatch()?.params.workspaceId;
  const workspaceId = workspaceIdFromStore ?? workspaceIdFromRoute;
  const licensingStatus = useSelector(getLicensingStatus);

  const verifyIfOrgHasAccessProtector = useCallback(() => {
    if (companyId && licensingStatus && systemFlags) {
      const appProtector = licensingStatus.apps.filter(app => app.id === PROTECTOR_APP_ID);
      if (appProtector.length === 0) {
        if (!window.location.href.includes('/unlicensed')) window.location.pathname = '/unlicensed';
        sessionStorage.setItem('unlicensed', 'true');
        sessionStorage.removeItem('refreshed');
      }
    }
  }, [licensingStatus, companyId, systemFlags]);

  useEffect(() => {
    if (companyId) {
      dispatch(LoadOrgLicensingStatus(companyId));
      dispatch(LoadCurrentWorkspaceId(companyId));

      if (!isCLevelReportPage) {
        dispatch(LoadProducts(companyId));
      }
    }
  }, [companyId, dispatch, isCLevelReportPage]);

  useEffect(() => {
    if (workspaceId) {
      dispatch(LoadWorkspaceLicensingStatus(workspaceId));
    }
  }, [workspaceId, dispatch]);

  useEffect(() => {
    if (systemFlags) {
      dispatch(
        LoadCompanies({
          pageable: {
            page: 0,
            size: 10
          }
        })
      );
    }
  }, [systemFlags, dispatch]);

  const [indexedDBInitialized, setIndexedDBInitialized] = useState(false);
  useEffect(() => {
    if (!indexedDBInitialized) {
      setIndexedDBInitialized(true);
      IndexedDBPromiseAPI.initializeIDB().then(
        () => console.info('IDB initialized!'),
        () => console.error('IndexedDB not supported')
      );
    }
  }, [indexedDBInitialized]);

  useEffect(() => {
    verifyIfOrgHasAccessProtector();
  }, [verifyIfOrgHasAccessProtector]);

  useEffect(() => {
    dispatch(SetUseNewMapColors(!!systemFlags?.P40_29700_show_statistic_legend));
  }, [systemFlags?.P40_29700_show_statistic_legend, dispatch]);

  useDidMount(() => {
    if (matchPath('/', window.location.pathname)) {
      navigate('/hall-of-companies');
    }
  });

  useEffect(() => {
    if (licensingStatus) {
      setShowZendesk(validatePlansAndEntitlements(licensingStatus, null, [Entitlements.ZENDESK, Entitlements.ZENDESK_LATAM]));
      const widgetkey = getZendeskWidgetKey(licensingStatus, [Entitlements.ZENDESK, Entitlements.ZENDESK_LATAM]);
      if (widgetkey === '') {
        setShowZendesk(false);
      } else {
        setzendeskWidgetKey(widgetkey);
      }
    }
  }, [licensingStatus]);

  useEffect(() => {
    setIsSiderVisible(!isMobile);
  }, [isMobile]);

  const handleHallFooterMenuClick = useCallback(() => {
    setIsSiderVisible(true);
    siderRef.current?.toggle();
  }, []);

  const shouldHideHeaderAndSider =
    isCLevelReportPage ||
    isVisitDashboardPage ||
    hideMenuInProductDetailsPath ||
    hideMenuInOrganizationCreatePath ||
    hideMenuInOrganizationEditPath ||
    isDiseaseRiskMFEPage ||
    isRiskManagementWebviewPage ||
    isReportsListWebviewPage ||
    isEmbeddedWebviewPage;

  const shouldRenderSider = !shouldHideHeaderAndSider && (isSiderVisible || !isMobile);
  const shouldHideZendesk = isRiskManagementWebviewPage;

  // Android WebView computes vh/svh viewport units as 0, collapsing `.st-layout` (height: 100svh)
  // into a blank page. Pin the height in px, scoped to the webview-embedded route only.
  const needsWebviewViewportHeight = isEmbeddedWebviewPage || isReportsListWebviewPage;
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight);
  useEffect(() => {
    if (!needsWebviewViewportHeight) return;
    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setViewportHeight(window.innerHeight));
    };
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
    };
  }, [needsWebviewViewportHeight]);

  return (
    <FeatureLoadTimingProvider pathname={pathname}>
      <Layout className='st-layout' style={needsWebviewViewportHeight ? { minHeight: viewportHeight } : undefined}>
        <Unless condition={shouldHideHeaderAndSider}>
          <TopBarContainer />
        </Unless>
        <Layout className='st-layout-internal'>
          <Unless condition={shouldHideHeaderAndSider}>
            <STSider
              ref={siderRef}
              hidden={!shouldRenderSider}
              isMobile={isMobile}
              hideSideBarInMobile={hideSideBarInMobile}
              onCollapseMobile={() => setIsSiderVisible(false)}
            />
          </Unless>
          <Content className='st-content'>
            <Suspense fallback={<Spin fullscreen />}>
              <Outlet />
            </Suspense>
          </Content>
          {showZendesk && !shouldHideZendesk && <ZendeskWidget widgetKey={zendeskWidgetKey} />}
          <Unless condition={shouldHideHeaderAndSider}>
            <HallFooterMobileButtons onMenuClick={handleHallFooterMenuClick} isMenuOpen={shouldRenderSider} />
          </Unless>
        </Layout>
      </Layout>
    </FeatureLoadTimingProvider>
  );
};

export default MainLayout;
